import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, apiClient } from '../../auth-lib';
import EmployeeLayout from '../components/EmployeeLayout';
import QuickAddTask from '../components/QuickAddTask';
import { Card, Button, Skeleton, StatusPill, Table, EmptyState } from '../../design-system';
import '../styles/Employee.css';
import { useLiveRefresh } from '../../lib/useLiveRefresh';

const DUE_SOON_DAYS = 7;

const formatTaskNumber = (t) =>
  t.taskNumber != null ? `TASK-${String(t.taskNumber).padStart(4, '0')}` : '-';

function EmployeeHome() {
  const revision = useLiveRefresh();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddVoice, setQuickAddVoice] = useState(false);

  // ⚠️ THE ANDROID HOME-SCREEN SHORTCUTS LAND HERE. manifest.json offers two:
  // /employee?new=task opens the dialog with the keyboard up, /employee?new=voice
  // opens it already listening. Long-pressing the installed icon therefore goes
  // straight to either one — no dashboard, no menu.
  //
  // ⚠️ THE SHORTCUT TAP IS THE USER GESTURE getUserMedia NEEDS, which is the
  // only reason voice mode can start recording without a second press. The
  // param is cleared immediately so a refresh, or a Back into this page, does
  // not reopen a dialog — or a microphone — the person already dealt with.
  useEffect(() => {
    const mode = searchParams.get('new');
    if (mode !== 'task' && mode !== 'voice') return;
    setQuickAddVoice(mode === 'voice');
    setQuickAddOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openQuickAdd = (withVoice = false) => {
    setQuickAddVoice(withVoice);
    setQuickAddOpen(true);
  };

  const loadTasks = useCallback(() => {
    apiClient
      .get('/api/tasks/my-tasks')
      .then((res) => setTasks(res.data))
      .catch((err) => console.error('Failed to fetch my tasks:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadTasks(); }, [revision, loadTasks]);

  // ⚠️ CAN THIS PERSON CREATE A TASK AT ALL? POST /api/tasks is behind
  // `tasks.create` (see routes/taskRoutes.js), so a role without it would get a
  // 403 from a button that looked perfectly willing. Hidden rather than shown
  // and refused — the same rule ProtectedRoute already applies, super admins
  // included.
  const canCreate = !!user?.isSuperAdmin || !!user?.permissions?.includes('tasks.create');

  const { stats, dueSoon, recent } = useMemo(() => {
    const now = new Date();
    const horizon = new Date(now.getTime() + DUE_SOON_DAYS * 24 * 60 * 60 * 1000);
    const open = tasks.filter((t) => t.status !== 'completed');

    return {
      stats: {
        total: tasks.length,
        todo: tasks.filter((t) => t.status === 'todo').length,
        inProgress: tasks.filter((t) => t.status === 'in-progress').length,
        review: tasks.filter((t) => t.status === 'review').length,
        completed: tasks.filter((t) => t.status === 'completed').length,
        overdue: open.filter((t) => t.dueDate && new Date(t.dueDate) < now).length,
      },
      dueSoon: open
        .filter((t) => t.dueDate && new Date(t.dueDate) <= horizon)
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
        .slice(0, 8),
      recent: tasks.slice(0, 5),
    };
  }, [tasks]);

  const statCards = [
    { key: 'total', label: 'Total Tasks' },
    { key: 'todo', label: 'To Do' },
    { key: 'inProgress', label: 'In Progress' },
    { key: 'review', label: 'In Review' },
    { key: 'completed', label: 'Completed' },
    { key: 'overdue', label: 'Overdue', accent: 'danger' },
  ];

  const dueSoonColumns = [
    { key: 'taskNumber', header: '#', render: formatTaskNumber },
    { key: 'title', header: 'Task', render: (t) => t.title },
    { key: 'status', header: 'Status', render: (t) => <StatusPill status={t.status} label={t.status === 'todo' ? 'Pending' : undefined} /> },
    {
      key: 'dueDate',
      header: 'Due',
      render: (t) => {
        const due = new Date(t.dueDate);
        const overdue = due < new Date();
        return (
          <span className={overdue ? 'employee-due-overdue' : undefined}>
            {due.toLocaleDateString()}
          </span>
        );
      },
    },
  ];

  return (
    <EmployeeLayout
      title={`Welcome, ${user?.name || 'there'}!`}
      subtitle="Your work at a glance"
      // In the header rather than down with Quick Actions: the whole point is
      // that it is reachable the second the page opens, without scrolling past
      // six stat cards. AppShell gives it full width below 900px.
      headerRight={canCreate ? (
        <Button variant="primary" onClick={() => openQuickAdd(false)}>+ Add Task</Button>
      ) : undefined}
    >
      <div className="employee-page">
        <div className="employee-stats-grid">
          {statCards.map((s) => (
            <Card key={s.key} className={`employee-stat-card${s.accent ? ` accent-${s.accent}` : ''}`}>
              {loading ? (
                <Skeleton height={32} width="50%" />
              ) : (
                <div className="employee-stat-number">{stats[s.key]}</div>
              )}
              <div className="employee-stat-label">{s.label}</div>
            </Card>
          ))}
        </div>

        <Card className="employee-section">
          <Card.Header title={`Due in the next ${DUE_SOON_DAYS} days`} subtitle="Open tasks with a due date, soonest first" />
          {!loading && dueSoon.length === 0 ? (
            <EmptyState icon="🎉" title="Nothing due soon" description="You're all caught up." />
          ) : (
            <Table
              columns={dueSoonColumns}
              data={dueSoon}
              loading={loading}
              onRowClick={(t) => navigate(`/tasks/${t._id}`)}
            />
          )}
        </Card>

        <Card className="employee-section">
          <Card.Header title="Recently assigned" />
          {!loading && recent.length === 0 ? (
            <EmptyState icon="📭" title="No tasks yet" description="Tasks assigned to you will appear here." />
          ) : (
            <Table
              columns={[
                { key: 'taskNumber', header: '#', render: formatTaskNumber },
                { key: 'title', header: 'Task', render: (t) => t.title },
                { key: 'priority', header: 'Priority', render: (t) => <StatusPill status={t.priority} /> },
                { key: 'status', header: 'Status', render: (t) => <StatusPill status={t.status} label={t.status === 'todo' ? 'Pending' : undefined} /> },
              ]}
              data={recent}
              loading={loading}
              onRowClick={(t) => navigate(`/tasks/${t._id}`)}
            />
          )}
        </Card>

        <Card className="employee-section">
          <Card.Header title="Quick Actions" />
          <div className="employee-section-actions">
            {canCreate && (
              <Button variant="primary" onClick={() => openQuickAdd(false)}>+ Add Task</Button>
            )}
            {/* The same dialog, opened listening. Here as well as on the home
                screen shortcut, because not everyone installs the PWA. */}
            {canCreate && (
              <Button variant="secondary" onClick={() => openQuickAdd(true)}>🎤 Speak a Task</Button>
            )}
            <Button variant="secondary" onClick={() => navigate('/employee/tasks')}>My Tasks</Button>
            <Button variant="secondary" onClick={() => navigate('/employee/todos')}>My To-Dos</Button>
            <Button variant="secondary" onClick={() => navigate('/compliance')}>Compliance Workspace</Button>
          </div>
        </Card>
      </div>

      {/* ⚠️ RELOADS RATHER THAN SPLICING THE NEW TASK IN. The dashboard's
          counts, the due-soon list and "recently assigned" are all derived from
          one array, and a locally appended task would be missing the
          taskNumber the pre-save hook assigns on the server. */}
      <QuickAddTask
        isOpen={quickAddOpen}
        voice={quickAddVoice}
        onClose={() => { setQuickAddOpen(false); setQuickAddVoice(false); }}
        onCreated={loadTasks}
      />
    </EmployeeLayout>
  );
}

export default EmployeeHome;
